import { useState, useEffect } from 'react';
import { companiesApi } from '@/api/companies';
import { accountsApi, Account } from '@/api/accounts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Modal, ModalFooter } from '@/components/ui/modal';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { format } from 'date-fns';
import { Check, X, Plus, Trash2 } from 'lucide-react';
import type { Company, CompanyCreateInput, CompanyUpdateInput } from '@/types/company';

interface DataSource {
  id?: number;
  name: string;
  utage_url: string;
  is_active: boolean;
}

export default function Companies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDataSourceModalOpen, setIsDataSourceModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [dataSourceCompany, setDataSourceCompany] = useState<Company | null>(null);
  const [dataSources, setDataSources] = useState<Account[]>([]);
  const [newDataSource, setNewDataSource] = useState<DataSource>({ name: '', utage_url: '', is_active: true });
  const [dataSourceError, setDataSourceError] = useState<string | null>(null);
  const [editingDataSourceId, setEditingDataSourceId] = useState<number | null>(null);
  const [editingDataSourceData, setEditingDataSourceData] = useState<DataSource>({ name: '', utage_url: '', is_active: true });

  const [createFormData, setCreateFormData] = useState<CompanyCreateInput>({
    name: '',
    display_name: '',
    utage_login_url: '',
    utage_email: '',
    utage_password: '',
    notes: '',
  });

  const [editFormData, setEditFormData] = useState<CompanyUpdateInput>({
    display_name: '',
    utage_login_url: '',
    utage_email: '',
    utage_password: '',
    notes: '',
  });

  const fetchCompanies = async () => {
    try {
      const response = await companiesApi.getCompanies();
      setCompanies(response.items);
    } catch (err) {
      console.error('法人一覧取得エラー:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const resetCreateForm = () => {
    setCreateFormData({
      name: '',
      display_name: '',
      utage_login_url: '',
      utage_email: '',
      utage_password: '',
      notes: '',
    });
    setError(null);
  };

  // エラーメッセージを文字列に変換するヘルパー
  const formatErrorMessage = (detail: any): string => {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
    }
    return '更新に失敗しました';
  };

  const handleCreate = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await companiesApi.createCompany(createFormData);
      setIsCreateModalOpen(false);
      resetCreateForm();
      fetchCompanies();
    } catch (err: any) {
      setError(formatErrorMessage(err.response?.data?.detail) || '法人の作成に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEdit = (company: Company) => {
    setEditingCompany(company);
    setEditFormData({
      display_name: company.display_name,
      utage_login_url: company.utage_login_url || '',
      utage_email: company.utage_email || '',
      utage_password: '',
      notes: company.notes || '',
    });
    setError(null);
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingCompany) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const updateData: CompanyUpdateInput = {};
      if (editFormData.display_name) updateData.display_name = editFormData.display_name;
      if (editFormData.utage_login_url) updateData.utage_login_url = editFormData.utage_login_url;
      if (editFormData.utage_email) updateData.utage_email = editFormData.utage_email;
      if (editFormData.utage_password) updateData.utage_password = editFormData.utage_password;
      if (editFormData.notes) updateData.notes = editFormData.notes;

      await companiesApi.updateCompany(editingCompany.id, updateData);
      setIsEditModalOpen(false);
      setEditingCompany(null);
      fetchCompanies();
    } catch (err: any) {
      setError(formatErrorMessage(err.response?.data?.detail) || '法人の更新に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (company: Company) => {
    try {
      await companiesApi.updateCompany(company.id, {
        is_active: !company.is_active,
      });
      fetchCompanies();
    } catch (err) {
      console.error('法人更新エラー:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('この法人を削除してもよろしいですか？関連するすべてのデータも削除されます。')) return;

    try {
      await companiesApi.deleteCompany(id);
      fetchCompanies();
    } catch (err) {
      console.error('法人削除エラー:', err);
    }
  };

  // データソース管理
  const handleOpenDataSources = async (company: Company) => {
    setDataSourceCompany(company);
    setDataSourceError(null);
    setNewDataSource({ name: '', utage_url: '', is_active: true });
    setEditingDataSourceId(null);
    try {
      const response = await accountsApi.getAccounts({ company_id: company.id });
      setDataSources(response.items);
    } catch (err) {
      console.error('データソース取得エラー:', err);
      setDataSources([]);
    }
    setIsDataSourceModalOpen(true);
  };

  const handleAddDataSource = async () => {
    if (!dataSourceCompany) return;
    if (!newDataSource.name.trim()) {
      setDataSourceError('タグ名を入力してください');
      return;
    }
    if (!newDataSource.utage_url.trim()) {
      setDataSourceError('URLを入力してください');
      return;
    }

    setIsSubmitting(true);
    setDataSourceError(null);

    try {
      const created = await accountsApi.createAccount({
        company_id: dataSourceCompany.id,
        name: newDataSource.name.trim(),
        utage_url: newDataSource.utage_url.trim(),
        is_active: true,
      });
      setDataSources([...dataSources, created]);
      setNewDataSource({ name: '', utage_url: '', is_active: true });
    } catch (err: any) {
      setDataSourceError(err.response?.data?.detail || '取得先の追加に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEditDataSource = (ds: Account) => {
    setEditingDataSourceId(ds.id);
    setEditingDataSourceData({
      name: ds.name,
      utage_url: ds.utage_url,
      is_active: ds.is_active,
    });
    setDataSourceError(null);
  };

  const handleCancelEditDataSource = () => {
    setEditingDataSourceId(null);
    setEditingDataSourceData({ name: '', utage_url: '', is_active: true });
  };

  const handleSaveDataSource = async () => {
    if (!editingDataSourceId) return;
    if (!editingDataSourceData.name.trim()) {
      setDataSourceError('タグ名を入力してください');
      return;
    }
    if (!editingDataSourceData.utage_url.trim()) {
      setDataSourceError('URLを入力してください');
      return;
    }

    setIsSubmitting(true);
    setDataSourceError(null);

    try {
      await accountsApi.updateAccount(editingDataSourceId, {
        name: editingDataSourceData.name.trim(),
        utage_url: editingDataSourceData.utage_url.trim(),
      });
      setDataSources(dataSources.map(ds =>
        ds.id === editingDataSourceId
          ? { ...ds, name: editingDataSourceData.name.trim(), utage_url: editingDataSourceData.utage_url.trim() }
          : ds
      ));
      setEditingDataSourceId(null);
    } catch (err: any) {
      setDataSourceError(err.response?.data?.detail || '取得先の更新に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleDataSource = async (account: Account) => {
    try {
      await accountsApi.updateAccount(account.id, { is_active: !account.is_active });
      setDataSources(dataSources.map(ds =>
        ds.id === account.id ? { ...ds, is_active: !ds.is_active } : ds
      ));
    } catch (err) {
      console.error('データソース更新エラー:', err);
    }
  };

  const handleDeleteDataSource = async (id: number) => {
    if (!confirm('この取得先を削除してもよろしいですか？')) return;

    try {
      await accountsApi.deleteAccount(id);
      setDataSources(dataSources.filter(ds => ds.id !== id));
    } catch (err) {
      console.error('データソース削除エラー:', err);
    }
  };

  // レスポンシブ用：法人カード表示（モバイル）
  const CompanyCard = ({ company }: { company: Company }) => (
    <div className="bg-white border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">{company.display_name}</h3>
          <p className="text-sm text-gray-500">{company.name}</p>
        </div>
        <Badge variant={company.is_active ? 'success' : 'default'}>
          {company.is_active ? '有効' : '無効'}
        </Badge>
      </div>

      <div className="text-sm space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-500">UTAGEメール:</span>
          <span className="text-gray-900 truncate ml-2 max-w-[200px]">{company.utage_email || '-'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">最終取得:</span>
          <span className="text-gray-900">
            {company.last_scraped_at
              ? format(new Date(company.last_scraped_at), 'yyyy/MM/dd HH:mm')
              : '-'}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2 border-t">
        <Button variant="outline" size="sm" onClick={() => handleOpenEdit(company)}>
          編集
        </Button>
        <Button variant="outline" size="sm" onClick={() => handleOpenDataSources(company)}>
          取得先
        </Button>
        <button
          onClick={() => handleToggleActive(company)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            company.is_active ? 'bg-green-500' : 'bg-gray-300'
          }`}
          title={company.is_active ? '無効にする' : '有効にする'}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              company.is_active ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
        <Button variant="danger" size="sm" onClick={() => handleDelete(company.id)} title="削除">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // データソースカード表示（モバイル）
  const DataSourceCard = ({ ds }: { ds: Account }) => {
    const isEditing = editingDataSourceId === ds.id;

    if (isEditing) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <Input
            label="タグ名"
            value={editingDataSourceData.name}
            onChange={(e) => setEditingDataSourceData({ ...editingDataSourceData, name: e.target.value })}
            placeholder="オプトイン、個別案内など"
          />
          <Input
            label="URL"
            value={editingDataSourceData.utage_url}
            onChange={(e) => setEditingDataSourceData({ ...editingDataSourceData, utage_url: e.target.value })}
            placeholder="https://..."
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSaveDataSource} isLoading={isSubmitting}>
              <Check className="w-4 h-4 mr-1" />
              保存
            </Button>
            <Button variant="outline" size="sm" onClick={handleCancelEditDataSource}>
              <X className="w-4 h-4 mr-1" />
              キャンセル
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white border rounded-lg p-4 space-y-2">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900">{ds.name}</h4>
            <a
              href={ds.utage_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline break-all"
            >
              {ds.utage_url}
            </a>
          </div>
          <Badge variant={ds.is_active ? 'success' : 'default'} className="ml-2 shrink-0">
            {ds.is_active ? '有効' : '無効'}
          </Badge>
        </div>
        <div className="flex items-center gap-3 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={() => handleStartEditDataSource(ds)}>
            編集
          </Button>
          <button
            onClick={() => handleToggleDataSource(ds)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              ds.is_active ? 'bg-green-500' : 'bg-gray-300'
            }`}
            title={ds.is_active ? '無効にする' : '有効にする'}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                ds.is_active ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <Button variant="danger" size="sm" onClick={() => handleDeleteDataSource(ds.id)} title="削除">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">法人管理</h1>
          <p className="text-sm sm:text-base text-gray-500">登録されている法人を管理します</p>
        </div>
        <Button onClick={() => { resetCreateForm(); setIsCreateModalOpen(true); }} className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-1" />
          新規法人登録
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>法人一覧</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              登録されている法人がありません
            </div>
          ) : (
            <>
              {/* デスクトップ: テーブル表示 */}
              <div className="hidden lg:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>法人名</TableHead>
                      <TableHead>表示名</TableHead>
                      <TableHead>UTAGEメール</TableHead>
                      <TableHead>ステータス</TableHead>
                      <TableHead>最終データ取得</TableHead>
                      <TableHead>操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companies.map((company) => (
                      <TableRow key={company.id}>
                        <TableCell className="font-medium">{company.name}</TableCell>
                        <TableCell>{company.display_name}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{company.utage_email || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={company.is_active ? 'success' : 'default'}>
                            {company.is_active ? '有効' : '無効'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {company.last_scraped_at
                            ? format(new Date(company.last_scraped_at), 'yyyy/MM/dd HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2 whitespace-nowrap items-center">
                            <Button variant="outline" size="sm" onClick={() => handleOpenEdit(company)}>
                              編集
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleOpenDataSources(company)}>
                              取得先
                            </Button>
                            <button
                              onClick={() => handleToggleActive(company)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                company.is_active ? 'bg-green-500' : 'bg-gray-300'
                              }`}
                              title={company.is_active ? '無効にする' : '有効にする'}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  company.is_active ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            <Button variant="danger" size="sm" onClick={() => handleDelete(company.id)} title="削除">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* モバイル/タブレット: カード表示 */}
              <div className="lg:hidden space-y-4">
                {companies.map((company) => (
                  <CompanyCard key={company.id} company={company} />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* 新規法人登録モーダル */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="新規法人登録"
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="法人名（システム内部名）"
            value={createFormData.name}
            onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
            placeholder="company_abc"
            helperText="英小文字、数字、ハイフン、アンダースコアのみ"
            required
          />
          <Input
            label="表示名"
            value={createFormData.display_name}
            onChange={(e) => setCreateFormData({ ...createFormData, display_name: e.target.value })}
            placeholder="株式会社ABC"
            required
          />
          <Input
            label="UTAGEログインURL"
            value={createFormData.utage_login_url || ''}
            onChange={(e) => setCreateFormData({ ...createFormData, utage_login_url: e.target.value })}
            placeholder="https://member.jyorei.net/operator/XXXXX/login"
          />
          <Input
            label="UTAGEメールアドレス"
            type="email"
            value={createFormData.utage_email || ''}
            onChange={(e) => setCreateFormData({ ...createFormData, utage_email: e.target.value })}
            placeholder="utage@example.com"
          />
          <Input
            label="UTAGEパスワード"
            type="password"
            value={createFormData.utage_password || ''}
            onChange={(e) => setCreateFormData({ ...createFormData, utage_password: e.target.value })}
            placeholder="パスワード"
          />
          <Input
            label="備考"
            value={createFormData.notes || ''}
            onChange={(e) => setCreateFormData({ ...createFormData, notes: e.target.value })}
            placeholder="メモ"
          />

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
            キャンセル
          </Button>
          <Button onClick={handleCreate} isLoading={isSubmitting}>
            登録
          </Button>
        </ModalFooter>
      </Modal>

      {/* 法人編集モーダル */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingCompany(null); }}
        title={editingCompany ? '法人編集: ' + editingCompany.display_name : '法人編集'}
        size="lg"
      >
        <div className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-500">法人名（システム内部名）</p>
            <p className="font-medium">{editingCompany?.name}</p>
          </div>
          <Input
            label="表示名"
            value={editFormData.display_name || ''}
            onChange={(e) => setEditFormData({ ...editFormData, display_name: e.target.value })}
            placeholder="株式会社ABC"
            required
          />
          <Input
            label="UTAGEログインURL"
            value={editFormData.utage_login_url || ''}
            onChange={(e) => setEditFormData({ ...editFormData, utage_login_url: e.target.value })}
            placeholder="https://member.jyorei.net/operator/XXXXX/login"
          />
          <Input
            label="UTAGEメールアドレス"
            type="email"
            value={editFormData.utage_email || ''}
            onChange={(e) => setEditFormData({ ...editFormData, utage_email: e.target.value })}
            placeholder="utage@example.com"
          />
          <Input
            label="UTAGEパスワード"
            type="password"
            value={editFormData.utage_password || ''}
            onChange={(e) => setEditFormData({ ...editFormData, utage_password: e.target.value })}
            placeholder="変更する場合のみ入力"
            helperText="変更しない場合は空欄のままにしてください"
          />
          <Input
            label="備考"
            value={editFormData.notes || ''}
            onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
            placeholder="メモ"
          />

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button variant="outline" onClick={() => { setIsEditModalOpen(false); setEditingCompany(null); }}>
            キャンセル
          </Button>
          <Button onClick={handleUpdate} isLoading={isSubmitting}>
            更新
          </Button>
        </ModalFooter>
      </Modal>

      {/* 取得先管理モーダル */}
      <Modal
        isOpen={isDataSourceModalOpen}
        onClose={() => { setIsDataSourceModalOpen(false); setDataSourceCompany(null); setEditingDataSourceId(null); }}
        title={dataSourceCompany ? '取得先ページ管理: ' + dataSourceCompany.display_name : '取得先ページ管理'}
        size="4xl"
      >
        <div className="space-y-4 sm:space-y-6">
          <p className="text-sm text-gray-600">
            UTAGEからデータを自動取得するページを複数登録できます。名前で分類し、それぞれのURLからデータを収集します。
          </p>

          {/* 取得先ページ一覧 */}
          {dataSources.length > 0 ? (
            <>
              {/* デスクトップ: テーブル表示 */}
              <div className="hidden md:block border rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700 w-40">タグ名</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">URL</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700 w-20">状態</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700 w-64">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {dataSources.map((ds) => (
                      <tr key={ds.id} className={editingDataSourceId === ds.id ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                        {editingDataSourceId === ds.id ? (
                          <>
                            <td className="px-4 py-2">
                              <Input
                                value={editingDataSourceData.name}
                                onChange={(e) => setEditingDataSourceData({ ...editingDataSourceData, name: e.target.value })}
                                placeholder="タグ名"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                value={editingDataSourceData.utage_url}
                                onChange={(e) => setEditingDataSourceData({ ...editingDataSourceData, utage_url: e.target.value })}
                                placeholder="URL"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Badge variant={ds.is_active ? 'success' : 'default'}>
                                {ds.is_active ? '有効' : '無効'}
                              </Badge>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex gap-1">
                                <Button size="sm" onClick={handleSaveDataSource} isLoading={isSubmitting} title="保存">
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleCancelEditDataSource} title="キャンセル">
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-2 font-medium whitespace-nowrap">{ds.name}</td>
                            <td className="px-4 py-2">
                              <a
                                href={ds.utage_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline break-all"
                                title={ds.utage_url}
                              >
                                {ds.utage_url}
                              </a>
                            </td>
                            <td className="px-4 py-2">
                              <Badge variant={ds.is_active ? 'success' : 'default'}>
                                {ds.is_active ? '有効' : '無効'}
                              </Badge>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex gap-3 whitespace-nowrap items-center">
                                <Button variant="outline" size="sm" onClick={() => handleStartEditDataSource(ds)}>
                                  編集
                                </Button>
                                <button
                                  onClick={() => handleToggleDataSource(ds)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                    ds.is_active ? 'bg-green-500' : 'bg-gray-300'
                                  }`}
                                  title={ds.is_active ? '無効にする' : '有効にする'}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      ds.is_active ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                                <Button variant="danger" size="sm" onClick={() => handleDeleteDataSource(ds.id)} title="削除">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* モバイル: カード表示 */}
              <div className="md:hidden space-y-3">
                {dataSources.map((ds) => (
                  <DataSourceCard key={ds.id} ds={ds} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-6 text-gray-500 border rounded-lg bg-gray-50">
              取得先ページが登録されていません
            </div>
          )}

          {/* 新規追加フォーム */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              新しい取得先を追加
            </h4>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="md:w-48 shrink-0">
                <Input
                  label="タグ名"
                  value={newDataSource.name}
                  onChange={(e) => setNewDataSource({ ...newDataSource, name: e.target.value })}
                  placeholder="オプトイン、個別案内など"
                />
              </div>
              <div className="flex-1">
                <Input
                  label="UTAGE URL"
                  value={newDataSource.utage_url}
                  onChange={(e) => setNewDataSource({ ...newDataSource, utage_url: e.target.value })}
                  placeholder="https://member.jyorei.net/account/XXXXX/label"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleAddDataSource} isLoading={isSubmitting}>
                  <Plus className="w-4 h-4 mr-1" />
                  追加
                </Button>
              </div>
            </div>
          </div>

          {dataSourceError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{dataSourceError}</p>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button variant="outline" onClick={() => { setIsDataSourceModalOpen(false); setDataSourceCompany(null); setEditingDataSourceId(null); }}>
            閉じる
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
